import React, { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { BarChart3, Target } from 'lucide-react';
import {
  AnalyticsStudentRecord,
  TermFilter,
  useGetFoundationCompetencyAnalyticsQuery,
} from '../../../services/holisticApi';
import { CompetencyExplorer } from './CompetencyExplorer';
import { FLNExplorer } from './FLNExplorer';

type TabKey = 'competency' | 'fln';

interface Props {
  records: AnalyticsStudentRecord[];
  term: TermFilter;
  classId?: number;
  /** Foundation "API year" (2026 → HPC academicYear 2025) for CG/competency/FLN analytics. */
  year?: number;
  /** Viewed region — passed at the region drill level. */
  roid?: number;
  /** Viewed school (KV) — passed at the school drill level. */
  kvid?: number;
  /**
   * Whether there is data to show. Defaults to `records.length > 0`. The national
   * dashboard passes it explicitly: it no longer downloads per-student records,
   * and at that level the tabs only ever read the aggregated rollup anyway.
   */
  hasData?: boolean;
}

const TABS: { key: TabKey; label: string; sub: string; icon: React.ReactNode }[] = [
  { key: 'competency', label: 'Competency Analytics', sub: 'Domain → Curricular Goal → Competency', icon: <BarChart3 size={16} /> },
  { key: 'fln',        label: 'FLN Assessment',        sub: 'Lakshya Entry vs Exit achievement',   icon: <Target size={16} /> },
];

export const FoundationMasterTabs: React.FC<Props> = ({ records, term, classId, year, roid, kvid, hasData }) => {
  const [tab, setTab] = useState<TabKey>('competency');

  // At class level the records already carry per-student rubric maps, so the
  // CG/competency/FLN analytics come from them directly — skip the aggregate call.
  // At national/region/school levels ("higher level"), fetch the per-competency
  // rubric rollup and let the explorers work off that pre-aggregated payload
  // instead of iterating the (very large) per-student records array.
  const higherLevel = classId === undefined;
  const { data: analyticsRes, isLoading, isFetching } = useGetFoundationCompetencyAnalyticsQuery(
    higherLevel && year != null ? { year, roid, kvid } : skipToken,
  );
  const analytics = analyticsRes?.data;
  const analyticsLoading = higherLevel && year != null && (isLoading || (isFetching && !analytics));

  if (!(hasData ?? records.length > 0)) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Master tab bar */}
      <div className="flex flex-col sm:flex-row border-b border-gray-100">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center gap-3 px-5 py-4 text-left transition relative ${
                active ? 'bg-indigo-50/40' : 'hover:bg-gray-50'
              }`}
            >
              <span className={`p-2 rounded-lg ${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{t.icon}</span>
              <span>
                <span className={`block text-sm font-bold ${active ? 'text-indigo-700' : 'text-gray-600'}`}>{t.label}</span>
                <span className="block text-[13px] text-gray-600">{t.sub}</span>
              </span>
              {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-indigo-600" />}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5">
        {tab === 'competency' && (
          <CompetencyExplorer
            records={records}
            term={term}
            analytics={analytics}
            preferCounts={higherLevel}
            loading={analyticsLoading}
          />
        )}
        {tab === 'fln' && (
          <FLNExplorer
            records={records}
            classId={classId}
            analytics={analytics}
            preferCounts={higherLevel}
            loading={analyticsLoading}
          />
        )}
      </div>
    </div>
  );
};
